import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FileUploadSection, QuickSettings, GeneratedQuestionsPreview } from './';
import PageSelector from './PageSelector';
import {
  splitTextIntoPages,
  addPageOverlap,
  mergeSelectedPages,
  getSelectionStats,
  autoSelectRelevantPages
} from '../../utils/llm-generator/documentSplitter';

/**
 * ChunkedWorkflow Component
 * Advanced workflow for splitting documents and selecting specific pages
 */
export default function ChunkedWorkflow({
  // File Upload Props
  llmProvider,
  useNativeFileUpload,
  setUseNativeFileUpload,
  uploadedFiles,
  fileContents,
  onFileUpload,
  onRemoveFile,
  formatFileSize,
  
  // Generation Settings
  numberOfQuestions,
  setNumberOfQuestions,
  difficulty,
  setDifficulty,
  includeCodeBlocks,
  setIncludeCodeBlocks,
  userGuidance,
  setUserGuidance,
  topicTags,
  currentTag,
  setCurrentTag,
  onAddTopicTag,
  onRemoveTopicTag,
  
  // Generation
  apiKey,
  isGenerating,
  generationLog,
  onGenerate,
  
  // Results
  generatedQuestions,
  setGeneratedQuestions,
  validationErrors,
  editingIndex,
  editedQuestion,
  setEditedQuestion,
  selectedSession,
  getQualityScore,
  findDuplicates,
  validateAllQuestions,
  onImportQuestions,
  onRerollQuestion,
  onGenerateVariation,
  onToggleMultipleChoice,
  onStartEditing,
  onSaveEdit,
  onCancelEdit,
  onSaveToSession,
  onDownloadQuestions,
  onRefineQuestion
}) {
  // Chunked workflow state
  const [charsPerPage, setCharsPerPage] = useState(3000);
  const [overlapChars, setOverlapChars] = useState(200);
  const [selectedPageIndices, setSelectedPageIndices] = useState([]);

  // Process uploaded files into pages
  const allPages = useMemo(() => {
    if (fileContents.length === 0) return [];
    
    // Combine all file contents
    const combinedContent = fileContents
      .map(f => `--- ${f.name} ---\n${f.content}`)
      .join('\n\n');
    
    // Split into pages
    const rawPages = splitTextIntoPages(combinedContent, { charsPerPage });
    
    // Add overlap information
    return addPageOverlap(rawPages, overlapChars);
  }, [fileContents, charsPerPage, overlapChars]);

  // Selection statistics
  const selectionStats = useMemo(() => {
    return getSelectionStats(allPages, selectedPageIndices);
  }, [allPages, selectedPageIndices]);

  // Get merged content for selected pages
  const selectedContent = useMemo(() => {
    return mergeSelectedPages(allPages, selectedPageIndices, {
      includeOverlap: true,
      separator: '\n\n---\n\n'
    });
  }, [allPages, selectedPageIndices]);

  // Handle auto-select by keywords
  const handleAutoSelect = useCallback((keywords) => {
    const relevantIndices = autoSelectRelevantPages(allPages, keywords, 5);
    setSelectedPageIndices(relevantIndices);
  }, [allPages]);

  // Handle generate with selected pages
  const handleGenerate = useCallback(() => {
    // Pass selected content to parent generate function
    onGenerate(selectedContent);
  }, [onGenerate, selectedContent]);

  // Auto-select first pages when files are uploaded
  React.useEffect(() => {
    if (allPages.length > 0 && selectedPageIndices.length === 0) {
      // Auto-select first 3 pages or all if less
      const autoSelect = allPages.slice(0, Math.min(3, allPages.length)).map((_, i) => i);
      setSelectedPageIndices(autoSelect);
    }
  }, [allPages.length]);

  const hasContent = selectedPageIndices.length > 0;
  const canGenerate = hasContent && (apiKey || llmProvider === 'custom');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left Column - Configuration */}
      <div className="space-y-4">
        {/* File Upload */}
        <FileUploadSection
          llmProvider={llmProvider}
          useNativeFileUpload={useNativeFileUpload}
          setUseNativeFileUpload={setUseNativeFileUpload}
          uploadedFiles={uploadedFiles}
          fileContents={fileContents}
          onFileUpload={onFileUpload}
          onRemoveFile={onRemoveFile}
          formatFileSize={formatFileSize}
        />

        {/* Page Splitter Settings */}
        {fileContents.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              ⚙️ Split-Einstellungen
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Zeichen/Seite
                </label>
                <select
                  value={charsPerPage}
                  onChange={(e) => {
                    setCharsPerPage(Number(e.target.value));
                    setSelectedPageIndices([]); // Reset selection when changing split
                  }}
                  className="w-full text-sm px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                >
                  <option value={1500}>~1.500 (klein)</option>
                  <option value={3000}>~3.000 (mittel)</option>
                  <option value={5000}>~5.000 (groß)</option>
                  <option value={8000}>~8.000 (sehr groß)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Überlappung
                </label>
                <select
                  value={overlapChars}
                  onChange={(e) => setOverlapChars(Number(e.target.value))}
                  className="w-full text-sm px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                >
                  <option value={0}>Keine</option>
                  <option value={100}>100 Zeichen</option>
                  <option value={200}>200 Zeichen</option>
                  <option value={500}>500 Zeichen</option>
                </select>
              </div>
            </div>
            
            <p className="text-xs text-gray-400 mt-2">
              {allPages.length} Seiten erkannt
            </p>
          </div>
        )}

        {/* Page Selector */}
        {allPages.length > 0 && (
          <PageSelector
            pages={allPages}
            selectedPages={selectedPageIndices}
            onSelectionChange={setSelectedPageIndices}
            stats={selectionStats}
            onAutoSelect={handleAutoSelect}
          />
        )}

        {/* Quick Settings */}
        <QuickSettings
          numberOfQuestions={numberOfQuestions}
          setNumberOfQuestions={setNumberOfQuestions}
          difficulty={difficulty}
          setDifficulty={setDifficulty}
          includeCodeBlocks={includeCodeBlocks}
          setIncludeCodeBlocks={setIncludeCodeBlocks}
          userGuidance={userGuidance}
          setUserGuidance={setUserGuidance}
          topicTags={topicTags}
          currentTag={currentTag}
          setCurrentTag={setCurrentTag}
          onAddTopicTag={onAddTopicTag}
          onRemoveTopicTag={onRemoveTopicTag}
        />

        {/* Generate Button */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <button
            onClick={handleGenerate}
            disabled={isGenerating || !canGenerate}
            className={`
              w-full py-3 px-6 rounded-lg font-semibold transition-all
              ${isGenerating || !canGenerate
                ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'bg-primary-600 text-white hover:bg-primary-700'
              }
            `}
          >
            {isGenerating ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Generiere aus {selectedPageIndices.length} Seiten...
              </span>
            ) : (
              <span>{numberOfQuestions} Fragen aus {selectedPageIndices.length} Seiten</span>
            )}
          </button>
          
          {!canGenerate && (
            <p className="text-xs text-center mt-2 text-gray-500 dark:text-gray-400">
              {selectedPageIndices.length === 0
                ? 'Seiten auswählen'
                : 'API Key fehlt - siehe Einstellungen'
              }
            </p>
          )}
        </div>

        {/* Selected Content Preview */}
        {selectedContent && (
          <details className="bg-gray-900 rounded-lg overflow-hidden">
            <summary className="px-4 py-2 text-xs text-gray-400 cursor-pointer hover:bg-gray-800">
              👁️ Ausgewählter Inhalt ({selectionStats.selectedChars.toLocaleString()} chars)
            </summary>
            <pre className="px-4 pb-4 text-gray-300 text-xs overflow-auto max-h-48 font-mono whitespace-pre-wrap">
              {selectedContent.slice(0, 2000)}
              {selectedContent.length > 2000 && '\n\n... (gekürzt)'}
            </pre>
          </details>
        )}

        {/* Generation Log */}
        {generationLog && (
          <details className="bg-gray-900 rounded-lg overflow-hidden">
            <summary className="px-4 py-2 text-xs text-gray-400 cursor-pointer hover:bg-gray-800">
              Log anzeigen
            </summary>
            <pre className="px-4 pb-4 text-success-400 text-xs overflow-auto max-h-32 font-mono whitespace-pre-wrap">
              {generationLog}
            </pre>
          </details>
        )}
      </div>

      {/* Right Column - Results */}
      <div>
        {generatedQuestions.length > 0 ? (
          <GeneratedQuestionsPreview
            questions={generatedQuestions}
            setQuestions={setGeneratedQuestions}
            validationErrors={validationErrors}
            editingIndex={editingIndex}
            editedQuestion={editedQuestion}
            setEditedQuestion={setEditedQuestion}
            isGenerating={isGenerating}
            selectedSession={selectedSession}
            getQualityScore={getQualityScore}
            findDuplicates={findDuplicates}
            validateAllQuestions={validateAllQuestions}
            onImportQuestions={onImportQuestions}
            onRerollQuestion={onRerollQuestion}
            onGenerateVariation={onGenerateVariation}
            onToggleMultipleChoice={onToggleMultipleChoice}
            onStartEditing={onStartEditing}
            onSaveEdit={onSaveEdit}
            onCancelEdit={onCancelEdit}
            onSaveToSession={onSaveToSession}
            onDownloadQuestions={onDownloadQuestions}
            onRefineQuestion={onRefineQuestion}
          />
        ) : (
          /* Empty State */
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 text-center h-full flex flex-col justify-center min-h-[300px]">
            <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
              <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
              Chunked Workflow
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
              Dokumente in Seiten aufteilen und gezielt Fragen generieren
            </p>
            
            <div className="text-left bg-gray-50 dark:bg-gray-900 rounded-lg p-4 text-xs text-gray-600 dark:text-gray-400">
              <p className="font-medium mb-2">So funktioniert's:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Dokument hochladen</li>
                <li>Seiten-Größe einstellen</li>
                <li>Relevante Seiten auswählen</li>
                <li>Fragen generieren</li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

ChunkedWorkflow.propTypes = {
  // File Upload
  llmProvider: PropTypes.string.isRequired,
  useNativeFileUpload: PropTypes.bool.isRequired,
  setUseNativeFileUpload: PropTypes.func.isRequired,
  uploadedFiles: PropTypes.array.isRequired,
  fileContents: PropTypes.array.isRequired,
  onFileUpload: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  formatFileSize: PropTypes.func.isRequired,
  
  // Generation Settings
  numberOfQuestions: PropTypes.number.isRequired,
  setNumberOfQuestions: PropTypes.func.isRequired,
  difficulty: PropTypes.string.isRequired,
  setDifficulty: PropTypes.func.isRequired,
  includeCodeBlocks: PropTypes.bool.isRequired,
  setIncludeCodeBlocks: PropTypes.func.isRequired,
  userGuidance: PropTypes.string,
  setUserGuidance: PropTypes.func.isRequired,
  topicTags: PropTypes.array.isRequired,
  currentTag: PropTypes.string.isRequired,
  setCurrentTag: PropTypes.func.isRequired,
  onAddTopicTag: PropTypes.func.isRequired,
  onRemoveTopicTag: PropTypes.func.isRequired,
  
  // Generation
  apiKey: PropTypes.string,
  isGenerating: PropTypes.bool.isRequired,
  generationLog: PropTypes.string,
  onGenerate: PropTypes.func.isRequired,
  
  // Results
  generatedQuestions: PropTypes.array.isRequired,
  setGeneratedQuestions: PropTypes.func.isRequired,
  validationErrors: PropTypes.array.isRequired,
  editingIndex: PropTypes.number,
  editedQuestion: PropTypes.object,
  setEditedQuestion: PropTypes.func.isRequired,
  selectedSession: PropTypes.object,
  getQualityScore: PropTypes.func.isRequired,
  findDuplicates: PropTypes.func.isRequired,
  validateAllQuestions: PropTypes.func.isRequired,
  onImportQuestions: PropTypes.func.isRequired,
  onRerollQuestion: PropTypes.func.isRequired,
  onGenerateVariation: PropTypes.func.isRequired,
  onToggleMultipleChoice: PropTypes.func.isRequired,
  onStartEditing: PropTypes.func.isRequired,
  onSaveEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onSaveToSession: PropTypes.func.isRequired,
  onDownloadQuestions: PropTypes.func.isRequired,
  onRefineQuestion: PropTypes.func
};
